import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import request from "../../../Helpers/API";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { Select } from "../Helper/Select";
import MultiSelectCheckbox from "../Helper/MultiSelectCheckbox";
import { CalendarIcon, ZapIcon } from "../Helper/Icons";
import { LoaderScreen } from "../../components/LoaderScreen";

/**
 * ProgramarInventarioModal
 *
 * Props:
 *   open                — bool
 *   onClose             — fn()
 *   onActualizar        — fn()  — refresca el listado al guardar
 *   listadoSucursales   — [{ value, label }]
 *   listadoResponsables — [{ value, label }]
 *   listadoAuditores    — [{ value, label }]
 *   listadoTipoInventario — [{ value, label }]
 *   listadoCategorias   — [{ value, label }]
 *   listadoEmpleados    — [{ value, label }]
 *   responsable         — array raw para buscar encargado por sucursal
 *   tipoInventario      — array raw para filtrar categorías por tipo
 *   onEditEncargados    — fn()  — abre modal de encargados
 *   SettingsIcon        — componente ícono
 */
const ProgramarInventarioModal = ({
    open,
    onClose,
    onActualizar,
    listadoSucursales = [],
    listadoResponsables = [],
    listadoAuditores = [],
    listadoTipoInventario = [],
    listadoCategorias: listadoCatInit = [],
    listadoEmpleados = [],
    responsable = [],
    tipoInventario = [],
    onEditEncargados,
    SettingsIconComp,
}) => {
    const today = new Date();

    const [loading, setLoading] = useState(false);
    const [sucursal, setSucursal] = useState("");
    const [tipoInv, setTipoInv] = useState("");
    const [categoria, setCategoria] = useState("");
    const [encargado, setEncargado] = useState("");
    const [auditor, setAuditor] = useState("");
    const [auditoresAd, setAuditoresAd] = useState("");
    const [fechaInicio, setFechaInicio] = useState(today.toISOString().slice(0, 10));
    const [fechaFin, setFechaFin] = useState(today.toISOString().slice(0, 10));
    const [listadoCategorias, setListadoCategorias] = useState(listadoCatInit);

    // Filtrar categorías cuando cambia el tipo de inventario
    useEffect(() => {
        const opcionesCat = tipoInventario
            .filter(ti => ti.idtipo === tipoInv)
            .map(ti => ({ value: ti.categoria, label: ti.categoria }));
        setListadoCategorias(opcionesCat);
        setCategoria(""); // limpiar selección anterior
    }, [tipoInv, tipoInventario]);

    // Autocompletar encargado al seleccionar sucursal
    useEffect(() => {
        const opcionResponsable = responsable.find(
            lr => lr.idsucursal === sucursal
        )?.idusuario;
        setEncargado(opcionResponsable ?? "");
    }, [sucursal, responsable]);

    // Limpiar al cerrar
    useEffect(() => {
        if (!open) {
            setSucursal("");
            setTipoInv("");
            setCategoria("");
            setEncargado("");
            setAuditor("");
            setAuditoresAd("");
            setFechaInicio(today.toISOString().slice(0, 10));
            setFechaFin(today.toISOString().slice(0, 10));
        }
    }, [open]);

    if (!open) return null;

    const handleGuardar = () => {
        if (!sucursal) {
            Swal.fire("PROGRAMAR INVENTARIO", "Debe seleccionar una sucursal", "warning");
            return;
        }
        if (!tipoInv) {
            Swal.fire("PROGRAMAR INVENTARIO", "Debe seleccionar un tipo de inventario", "warning");
            return;
        }
        if (!fechaInicio) {
            Swal.fire("PROGRAMAR INVENTARIO", "Debe seleccionar la fecha de inicio", "warning");
            return;
        }
        if (!fechaFin) {
            Swal.fire("PROGRAMAR INVENTARIO", "Debe seleccionar la fecha de fin", "warning");
            return;
        }
        if (!encargado) {
            Swal.fire("PROGRAMAR INVENTARIO", "Debe seleccionar un encargado de almacén", "warning");
            return;
        }
        if (!auditor) {
            Swal.fire("PROGRAMAR INVENTARIO", "Debe seleccionar un auditor principal", "warning");
            return;
        }
        if (!categoria || categoria.length === 0) {
            Swal.fire("PROGRAMAR INVENTARIO", "Debe seleccionar al menos una categoría", "warning");
            return;
        }

        setLoading(true);

        const post = {
            id_sucursal: sucursal,
            tipo_inventario: tipoInv,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            fecha_limite_just: "",
            id_encargado_alm: encargado,
            id_auditor_ppal: auditor,
            observaciones: "",
            auditores_adicionales: auditoresAd,
            categorias: categoria,
        };

        request
            .post("ProgramarInventario/Guardar", post)
            .then(async (result) => {
                setLoading(false);
                if (result.resultado === 1) {
                    await Swal.fire("PROGRAMAR INVENTARIO", result.mensaje, "error");
                } else {
                    await Swal.fire("PROGRAMAR INVENTARIO", "Programación registrada con éxito", "success");
                    onActualizar?.();
                    onClose?.();
                }
            })
            .catch((error) => {
                setLoading(false);
                Swal.fire("PROGRAMAR INVENTARIO", JSON.stringify(error), "error");
            });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    return (
        <div className="ram-backdrop" onClick={handleBackdropClick}>
            <PlantillaScreen
                titulo="Programar inventario"
                className="ram-modal pim-modal"
            >
                <button className="ram-close" onClick={onClose} type="button">X</button>
                <LoaderScreen loader={loading} />

                {/* ── Body ── */}
                <div className="ram-body pim-body">

                    {/* Grid simétrico — misma estructura que tenías */}
                    <div className="pim-grid">

                        {/* ── Col 1 ── */}
                        <Select
                            label="Sucursal"
                            options={listadoSucursales}
                            value={sucursal}
                            onChange={setSucursal}
                            placeholder="Seleccione una Sucursal"
                        />
                        <Select
                            label="Encargado almacén"
                            options={listadoResponsables}
                            value={encargado}
                            onChange={setEncargado}
                            placeholder="Seleccione un encargado"
                            disabled
                            icon={SettingsIconComp ? <SettingsIconComp /> : undefined}
                            iconClick={onEditEncargados}
                        />

                        {/* ── Col 2 ── */}
                        <Select
                            label="Auditor principal"
                            options={listadoAuditores}
                            value={auditor}
                            onChange={setAuditor}
                            placeholder="Seleccione un Auditor"
                        />



                        {/* ── Col 3 ── */}
                        <Select
                            label="Tipo inventario"
                            options={listadoTipoInventario}
                            value={tipoInv}
                            onChange={setTipoInv}
                            placeholder="Seleccione tipo"
                        />

                        <MultiSelectCheckbox
                            label="Categoría"
                            options={listadoCategorias}
                            value={categoria}
                            onChange={setCategoria}
                            placeholder="Categorías"
                        />

                        {/* Fechas */}
                        <div className="pa-date-group">
                            <div className="pa-date-field">
                                <label className="pa-label--sm">Fecha de inicio</label>
                                <div className="pa-input-wrap">
                                    <CalendarIcon />
                                    <input
                                        type="date"
                                        value={fechaInicio}
                                        onChange={e => setFechaInicio(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="pa-date-field">
                                <label className="pa-label--sm">Fecha de terminación</label>
                                <div className="pa-input-wrap">
                                    <CalendarIcon />
                                    <input
                                        type="date"
                                        value={fechaFin}
                                        onChange={e => setFechaFin(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ── Col 4 ── */}
                        <MultiSelectCheckbox
                            label="Auditores adicionales"
                            options={listadoEmpleados}
                            value={auditoresAd}
                            onChange={setAuditoresAd}
                            placeholder="Auditores adicionales"
                        />

                        {/* Spacer para mantener simetría */}
                        <div />

                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="ram-footer">
                    <button
                        type="button"
                        className="ram-btn ram-btn--ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="ram-btn ram-btn--primary"
                        onClick={handleGuardar}
                        disabled={loading}
                    >
                        {loading ? <span className="ram-btn__spinner" /> : <ZapIcon />}
                        {loading ? "Guardando..." : "Generar"}
                    </button>
                </div>

            </PlantillaScreen>
        </div>
    );
};

export default ProgramarInventarioModal;
